import { createSlice } from "@reduxjs/toolkit";
import { toast } from 'react-toastify';
import { ApiStatus } from "../../utils/api.types";
import { ExpensePayment, ExpenseCategory, ExpenseCategoryDropdownItem, Vendor } from "../../utils/expense.types";
import {
    fetchExpensePayments,
    recordExpensePayment,
    settleExpensePayment,
    deleteExpensePayment,
    fetchExpenseCategories,
    exportExpenseReport,
    fetchExpenseVendors,
    createExpenseCategory,
    batchSettleExpenses
} from "./expenseThunk";

export interface ExpenseState {
    payments?: ExpensePayment[];
    rowCount: number;
    categories?: ExpenseCategory[];
    categoryDropdownItems?: ExpenseCategoryDropdownItem[];
    vendors?: Vendor[];
    status?: ApiStatus;
    errorMessage?: string;
}

/**
 * Expense slice initial state.
 */
const initialState: ExpenseState = {
    status: ApiStatus.IDLE,
    errorMessage: '',
    payments: [],
    rowCount: 0,
    categories: [],
    categoryDropdownItems: [],
    vendors: []
}

/**
 * Expense Slice.
 */
const expenseSlice = createSlice({
    name: 'expense',
    initialState: initialState,
    reducers: {
        clearExpenseError: (state) => {
            state.errorMessage = '';
        },
        resetExpenseState: () => initialState
    },
    extraReducers: (builder) => {
        builder
            // Fetch expense payments
            .addCase(fetchExpensePayments.pending, (state) => {
                state.status = ApiStatus.LOADING;
            })
            .addCase(fetchExpensePayments.fulfilled, (state, action) => {
                state.status = ApiStatus.SUCCESS;
                state.payments = action?.payload?.payments || [];
                state.rowCount = action?.payload?.total || 0;
                state.errorMessage = '';
            })
            .addCase(fetchExpensePayments.rejected, (state, action) => {
                state.status = ApiStatus.FAILED;
                state.errorMessage = action.error.message || 'Failed to fetch expense payments';
            })

            // Record expense payment
            .addCase(recordExpensePayment.pending, (state) => {
                state.status = ApiStatus.LOADING;
            })
            .addCase(recordExpensePayment.fulfilled, (state, action) => {
                state.status = ApiStatus.SUCCESS;
                // Add new payment to the beginning of the list
                if (state.payments) {
                    state.payments.unshift(action.payload);
                } else {
                    state.payments = [action.payload];
                }
                toast.success('Expense payment recorded successfully');
                state.errorMessage = '';
            })
            .addCase(recordExpensePayment.rejected, (state, action) => {
                state.status = ApiStatus.FAILED;
                state.errorMessage = action.error.message || 'Failed to record expense payment';
                toast.error(action.error.message || 'Failed to record expense payment');
            })

            // Settle expense payment
            .addCase(settleExpensePayment.pending, (state) => {
                state.status = ApiStatus.LOADING;
            })
            .addCase(settleExpensePayment.fulfilled, (state, action) => {
                state.status = ApiStatus.SUCCESS;
                // Update the payment in the list
                if (state.payments) {
                    const index = state.payments.findIndex(p => p._id === action.payload._id);
                    if (index !== -1) {
                        state.payments[index] = action.payload;
                    }
                }
                toast.success('Expense settled successfully');
                state.errorMessage = '';
            })
            .addCase(settleExpensePayment.rejected, (state, action) => {
                state.status = ApiStatus.FAILED;
                state.errorMessage = action.error.message || 'Failed to settle expense payment';
                toast.error(action.error.message || 'Failed to settle expense payment');
            })

            // Delete expense payment
            .addCase(deleteExpensePayment.pending, (state) => {
                state.status = ApiStatus.LOADING;
            })
            .addCase(deleteExpensePayment.fulfilled, (state, action) => {
                state.status = ApiStatus.SUCCESS;
                const { message } = action.payload;
                toast.success(message || 'Expense payment deleted successfully');
                state.errorMessage = '';
            })
            .addCase(deleteExpensePayment.rejected, (state, action) => {
                state.status = ApiStatus.FAILED;
                state.errorMessage = action.error.message || 'Failed to delete expense payment';
                toast.error(action.error.message || 'Failed to delete expense payment');
            })

            // Fetch expense categories
            .addCase(fetchExpenseCategories.pending, (state) => {
                state.status = ApiStatus.LOADING;
            })
            .addCase(fetchExpenseCategories.fulfilled, (state, action) => {
                state.status = ApiStatus.SUCCESS;
                state.categories = action.payload;
                // Transform categories to dropdown items
                state.categoryDropdownItems = action.payload.map(cat => ({
                    value: cat._id,
                    label: cat.label,
                    category: cat.name,
                    code: cat.code,
                    color: cat.color || '#6366f1',
                    icon: cat.icon,
                    description: cat.description,
                    isSubcategory: false
                }));
                state.errorMessage = '';
            })
            .addCase(fetchExpenseCategories.rejected, (state, action) => {
                state.status = ApiStatus.FAILED;
                state.errorMessage = action.error.message || 'Failed to fetch expense categories';
            })

            // Export expense report
            .addCase(exportExpenseReport.pending, (state) => {
                state.status = ApiStatus.LOADING;
            })
            .addCase(exportExpenseReport.fulfilled, (state) => {
                state.status = ApiStatus.SUCCESS;
                toast.success('Report exported successfully');
                state.errorMessage = '';
            })
            .addCase(exportExpenseReport.rejected, (state, action) => {
                state.status = ApiStatus.FAILED;
                state.errorMessage = action.error.message || 'Failed to export report';
                toast.error(action.error.message || 'Failed to export report');
            })

            // Fetch expense vendors
            .addCase(fetchExpenseVendors.pending, (state) => {
                state.status = ApiStatus.LOADING;
            })
            .addCase(fetchExpenseVendors.fulfilled, (state, action) => {
                state.status = ApiStatus.SUCCESS;
                state.vendors = action.payload;
                state.errorMessage = '';
            })
            .addCase(fetchExpenseVendors.rejected, (state, action) => {
                state.status = ApiStatus.FAILED;
                state.errorMessage = action.error.message || 'Failed to fetch vendors';
            })
            
            // Create expense category
            .addCase(createExpenseCategory.pending, (state) => {
                state.status = ApiStatus.LOADING;
            })
            .addCase(createExpenseCategory.fulfilled, (state, action) => {
                state.status = ApiStatus.SUCCESS;
                // Add new category to the list
                if (state.categories) {
                    state.categories.push(action.payload);
                }
                // Update dropdown items
                const newDropdownItem: ExpenseCategoryDropdownItem = {
                    value: action.payload._id,
                    label: action.payload.parentCategory ? 
                        `${action.payload.parentCategory.name} > ${action.payload.name}` : 
                        action.payload.name,
                    category: action.payload.name,
                    code: action.payload.code,
                    color: action.payload.color || '#6366f1',
                    icon: action.payload.icon,
                    description: action.payload.description,
                    isSubcategory: !!action.payload.parentCategory
                };
                if (state.categoryDropdownItems) {
                    state.categoryDropdownItems.push(newDropdownItem);
                }
                toast.success('Category created successfully');
            })
            .addCase(createExpenseCategory.rejected, (state, action) => {
                state.status = ApiStatus.FAILED;
                state.errorMessage = action.error.message || 'Failed to create expense category';
                toast.error('Failed to create category');
            })
            // Batch Settle Expenses
            .addCase(batchSettleExpenses.fulfilled, (state, action) => {
                state.status = ApiStatus.SUCCESS;
                toast.success(action.payload?.message || 'Expenses settled successfully');
            })
            .addCase(batchSettleExpenses.rejected, (state, action) => {
                state.status = ApiStatus.FAILED;
                toast.error(action.error.message || 'Failed to settle expenses');
            })
    }
});

export const { clearExpenseError, resetExpenseState } = expenseSlice.actions;

export default expenseSlice.reducer;